/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { MediaLibraryModal } from '@molecules';
import { isCroppable, encodeCrop } from '@utils/media';

import SelectMedia from './SelectMedia';
import CropImage from './CropImage';

const STEP = {
  SELECT: 0,
  CROP: 1,
};

const getAspect = ({ aspect, size }) => {
  if (aspect) return aspect;
  if (size) return size.width / size.height;

  return null;
};

const AddMediaModal = ({
  visible,
  hide,
  type,
  initialMedia,
  initialCrop,
  aspect,
  size,
  onAddMedia,
  skipCrop,
  ...props
}) => {
  const [media, setMedia] = React.useState(initialMedia);
  const [crop, setCrop] = React.useState(initialCrop);

  const shouldCrop = React.useCallback((_media) => !skipCrop && isCroppable(_media), [skipCrop]);

  const initialStep = shouldCrop(initialMedia) ? STEP.CROP : STEP.SELECT;
  const [step, setStep] = React.useState(initialStep);

  React.useEffect(() => {
    if (visible) {
      setMedia(initialMedia);
      setStep(initialStep);
      setCrop(initialCrop);
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = React.useCallback(
    (selectedMedia) => {
      if (shouldCrop(selectedMedia)) {
        setMedia(selectedMedia);
        setStep(STEP.CROP);
      } else {
        onAddMedia({ id: selectedMedia.id });
        hide();
      }
    },
    [hide, onAddMedia, shouldCrop],
  );

  const handleCancel = React.useCallback(() => {
    if (step === STEP.SELECT && shouldCrop(initialMedia)) {
      setStep(STEP.CROP);
    } else {
      hide();
    }
  }, [hide, initialMedia, shouldCrop, step]);

  const handleOnChangeImage = React.useCallback(() => {
    setStep(STEP.SELECT);
  }, []);

  const handleFinish = React.useCallback(() => {
    onAddMedia({
      id: media.id,
      fileName: media.fileName,
      title: media.title,
      ...(shouldCrop(media) ? { edited: encodeCrop({ media, edits: { extract: crop, resize: size } }) } : null),
    });
    hide();
  }, [crop, hide, media, onAddMedia, shouldCrop, size]);

  const okButtonText = initialCrop ? 'UPDATE IMAGE' : 'CROP IMAGE';

  return (
    <MediaLibraryModal
      visible={visible}
      hide={hide}
      onCancel={handleCancel}
      okText={okButtonText}
      onOk={handleFinish}
      okHidden={step === STEP.SELECT}
    >
      {step === STEP.SELECT && <SelectMedia type={type} onSelect={handleSelect} />}
      {step === STEP.CROP && shouldCrop(media) && (
        <CropImage
          media={media}
          initialAspect={getAspect({ aspect, size })}
          initialCrop={initialCrop}
          onChangeImage={handleOnChangeImage}
          onComplete={setCrop}
        />
      )}
    </MediaLibraryModal>
  );
};

export default AddMediaModal;
